import { useState, useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { DotsThreeVertical } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import toast from 'react-hot-toast';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import {
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
  useDisableMemberMdm,
  useEnableMemberMdm,
  useTransferOwnership,
} from 'src/api/queries/enterpriseAdmin';
import { queryFleetMembers, useFleetMembers } from 'src/api/queries/fleet';
import { queryKeys } from 'src/api/queries/queryKeys';
import {
  queryAccessTokenClaims,
  useAccessTokenClaims,
} from 'src/api/queries/user';
import { Button, Modal, NotificationBox } from 'src/components';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
} from 'src/components/DropdownMenu';
import { authHelpers } from 'src/utils/authHelpers';
import { userHelpers } from 'src/utils/userHelpers';
import { ConfirmDeactivationModal } from './ConfirmDeactivationModal';
import { ConfirmDeletionModal } from './ConfirmDeletionModal';
import { ConfirmMdmDeactivationModal } from './ConfirmMdmDeactivationModal';
import { fleetUserHelpers } from './fleetUserHelpers';
import { TransferOwnershipModal } from './TransferOwnershipModal';

type FleetRole = NonNullable<FleetUser['roles']>[number];

export type MemberActionMenuOwnershipContext = {
  currentUserEmail?: string;
  currentUserOwnerRole?: FleetRole | null;
};

async function logoutAfterOwnershipTransfer(
  auth0: ReturnType<typeof useAuth0>
) {
  try {
    await auth0.logout({
      logoutParams: {
        returnTo: globalThis.location.origin,
      },
    });
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function useToggleUserStatus(
  member: FleetUser,
  closeMemberDrawer?: () => void
) {
  const queryClient = useQueryClient();
  const activateUser = useActivateUser();
  const deactivateUser = useDeactivateUser();
  const deleteUser = useDeleteUser();

  const optimisticStatusUpdate = (
    memberId: string,
    newStatus: FleetUser['status']
  ) => {
    // Snapshot the previous value
    const previousFleetMembers = queryClient.getQueryData(
      queryKeys.fleet.members
    ) as { users: FleetUser[] };

    // Optimistically update to the new value
    if (previousFleetMembers) {
      queryClient.setQueryData(queryKeys.fleet.members, {
        ...previousFleetMembers,
        users: previousFleetMembers.users.map((user: FleetUser) =>
          user.id === memberId ? { ...user, status: newStatus } : user
        ),
      });
    }
  };

  const toggleStatus = (newStatus: FleetUser['status']) => {
    let apiCall;
    let successTitle;
    let successMessage;

    switch (newStatus) {
      case 'active':
        apiCall = activateUser;
        successTitle = 'User re-activated';
        successMessage =
          'The user has been successfully re-activated. The user can now be paired with a paper tablet.';
        break;
      case 'deactivated':
        apiCall = deactivateUser;
        successTitle = 'User deactivated';
        successMessage =
          'The user has been successfully deactivated and all paper tablets have been unpaired from the user account.';
        break;
      case 'deletion_mark':
        apiCall = deleteUser;
        successTitle = 'User marked for deletion';
        successMessage =
          'The user has been successfully marked for deletion and will be deleted within 7 days.';
        break;
      default:
        throw new Error(`Unsupported status: ${newStatus ?? 'undefined'}`);
    }

    apiCall.mutate(
      { memberId: member.id },
      {
        onSuccess: (_data, { memberId }) => {
          optimisticStatusUpdate(memberId, newStatus);
          if (closeMemberDrawer) {
            closeMemberDrawer();
          }
          toast.custom(
            <NotificationBox
              variant="success"
              title={successTitle}
              className="max-w-md shadow-md"
            >
              <Typography variant="body-md">{successMessage}</Typography>
            </NotificationBox>
          );
        },
      }
    );
  };

  return { toggleStatus };
}

export function useToggleMemberMdm(
  member: FleetUser,
  closeMemberDrawer?: () => void
) {
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const enableMemberMdm = useEnableMemberMdm();
  const disableMemberMdm = useDisableMemberMdm();

  const optimisticMdmUpdate = (memberId: string, mdmDisabled: boolean) => {
    queryClient.setQueryData(queryFleetMembers().queryKey, (old) =>
      old
        ? {
            ...old,
            users: old.users?.map((user) =>
              user.id === memberId ? { ...user, mdmDisabled } : user
            ),
          }
        : old
    );
  };

  const toggleMdm = (mdmDisabled: boolean) => {
    const apiCall = mdmDisabled ? disableMemberMdm : enableMemberMdm;
    const successTitle = mdmDisabled ? t`MDM deactivated` : t`MDM activated`;
    const successMessage = mdmDisabled
      ? t`The user is no longer subject to MDM enforcement.`
      : t`The user is subject to MDM enforcement again.`;

    apiCall.mutate(
      { memberId: member.id },
      {
        onSuccess: (_data, { memberId }) => {
          optimisticMdmUpdate(memberId, mdmDisabled);
          if (closeMemberDrawer) {
            closeMemberDrawer();
          }
          toast.custom(
            <NotificationBox
              variant="success"
              title={successTitle}
              className="max-w-md shadow-md"
            >
              <Typography variant="body-md">{successMessage}</Typography>
            </NotificationBox>
          );
        },
      }
    );
  };

  return { toggleMdm };
}

export function MemberActionMenu({
  member,
  closeMemberDrawer,
  ownershipContext,
}: {
  member: FleetUser;
  closeMemberDrawer?: () => void;
  ownershipContext?: MemberActionMenuOwnershipContext;
}) {
  const [confirmDeactivationModalOpen, setConfirmDeactivationModalOpen] =
    useState<boolean>(false);
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] =
    useState<boolean>(false);
  const [confirmMdmDeactivationModalOpen, setConfirmMdmDeactivationModalOpen] =
    useState<boolean>(false);
  const [
    confirmTransferOwnershipModalOpen,
    setConfirmTransferOwnershipModalOpen,
  ] = useState<boolean>(false);

  const accessTokenClaims = useAccessTokenClaims();
  const queryClient = useQueryClient();
  const { toggleStatus } = useToggleUserStatus(member, closeMemberDrawer);
  const { toggleMdm } = useToggleMemberMdm(member, closeMemberDrawer);
  const fleetMembers = useFleetMembers();
  const transferOwnership = useTransferOwnership();
  const auth0 = useAuth0();

  const isMemberDeactivated = member.status === 'deactivated';
  const isMemberMarkedForDeletion = member.status === 'deletion_mark';
  const isMemberActive = member.status === 'active';
  const isMemberMdmDisabled = Boolean(member.mdmDisabled);

  const canToggleUserStatus = userHelpers.hasPermissionChangeUserStatus(
    accessTokenClaims.data
  );

  const canMarkForDeletion = canToggleUserStatus && isMemberDeactivated;

  // Check if current user has owner role
  const claimsCurrentUserEmail = userHelpers.getEmailFromClaims(
    accessTokenClaims.data
  );
  const currentUserEmail =
    ownershipContext?.currentUserEmail ?? claimsCurrentUserEmail;

  const shouldResolveCurrentUserFromMembers =
    ownershipContext?.currentUserOwnerRole === undefined;

  const currentUser = useMemo(
    () =>
      shouldResolveCurrentUserFromMembers
        ? fleetMembers.data?.users?.find((u) => u.email === currentUserEmail)
        : undefined,
    [
      shouldResolveCurrentUserFromMembers,
      fleetMembers.data?.users,
      currentUserEmail,
    ]
  );
  const derivedCurrentUserHasOwnerRole = useMemo(
    () =>
      currentUser?.roles?.some(
        (role) => fleetUserHelpers.getRoleType(role.name) === 'OWNER'
      ),
    [currentUser?.roles]
  );
  const currentUserHasOwnerRole =
    ownershipContext?.currentUserOwnerRole !== undefined
      ? Boolean(ownershipContext.currentUserOwnerRole)
      : derivedCurrentUserHasOwnerRole;

  // Find owner role ID from current user's roles (since it's not in available roles)
  const derivedOwnerRole = useMemo(() => {
    return currentUser?.roles?.find(
      (role) => fleetUserHelpers.getRoleType(role.name) === 'OWNER'
    );
  }, [currentUser?.roles]);
  const ownerRole =
    ownershipContext?.currentUserOwnerRole !== undefined
      ? ownershipContext.currentUserOwnerRole
      : derivedOwnerRole;

  // Check if member already has owner role
  const memberHasOwnerRole = member.roles?.some(
    (role) => fleetUserHelpers.getRoleType(role.name) === 'OWNER'
  );

  // Can transfer ownership if:
  // - Current user has owner role
  // - Member is active
  // - Member is not the current user
  // - Member doesn't already have owner role
  const canTransferOwnership =
    currentUserHasOwnerRole &&
    isMemberActive &&
    member.email !== currentUserEmail &&
    !memberHasOwnerRole &&
    ownerRole &&
    userHelpers.hasPermissionCreateRoleAssignments(accessTokenClaims.data);

  // in that case we don't show the menu at all as no actions are available
  if (
    (!canToggleUserStatus && !canTransferOwnership) ||
    isMemberMarkedForDeletion
  ) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            variant="tertiary-neutral"
            className="!rounded data-[state=open]:bg-interaction-tertiary-active"
            size="small"
            data-cy={`actions-button-cy_${member.email ?? 'unknown'}`}
            analytics={{
              text: 'DotsThreeVertical icon',
              location: ComponentLocations.FLEET.FLEET_MEMBERS_TABLE,
              action: 'open member actions menu',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DotsThreeVertical />
          </Button>
        }
        onClick={(e) => e.stopPropagation()}
      >
        {canTransferOwnership && (
          <DropdownMenuGroup>
            <DropdownMenuLabel>Ownership</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => setConfirmTransferOwnershipModalOpen(true)}
            >
              Transfer ownership
            </DropdownMenuItem>
          </DropdownMenuGroup>
        )}
        {canToggleUserStatus && !isMemberMarkedForDeletion && (
          <DropdownMenuGroup>
            <DropdownMenuLabel>Account</DropdownMenuLabel>

            <DropdownMenuItem
              onClick={() => {
                isMemberDeactivated
                  ? toggleStatus('active')
                  : setConfirmDeactivationModalOpen(true);
              }}
            >
              {isMemberDeactivated ? 'Re-activate' : 'Deactivate'}
            </DropdownMenuItem>
            {canMarkForDeletion && (
              <DropdownMenuItem
                onClick={() => setConfirmDeletionModalOpen(true)}
              >
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        )}
        {canToggleUserStatus && !isMemberMarkedForDeletion && (
          <DropdownMenuGroup data-cy="mdm-actions">
            <DropdownMenuLabel>
              <Trans>MDM</Trans>
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                isMemberMdmDisabled
                  ? toggleMdm(false)
                  : setConfirmMdmDeactivationModalOpen(true);
              }}
            >
              {isMemberMdmDisabled ? (
                <Trans>Activate</Trans>
              ) : (
                <Trans>Deactivate</Trans>
              )}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        )}
      </DropdownMenu>
      <Modal.Root
        open={confirmDeactivationModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDeactivationModalOpen(false);
          }
        }}
      >
        <ConfirmDeactivationModal
          member={member}
          closeModal={() => setConfirmDeactivationModalOpen(false)}
          handleDeactivation={() => {
            toggleStatus('deactivated');
          }}
        />
      </Modal.Root>
      <Modal.Root
        open={confirmDeletionModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDeletionModalOpen(false);
          }
        }}
      >
        <ConfirmDeletionModal
          member={member}
          closeModal={() => setConfirmDeletionModalOpen(false)}
          handleDeletion={() => {
            toggleStatus('deletion_mark');
          }}
        />
      </Modal.Root>
      <Modal.Root
        open={confirmMdmDeactivationModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmMdmDeactivationModalOpen(false);
          }
        }}
      >
        <ConfirmMdmDeactivationModal
          member={member}
          closeModal={() => setConfirmMdmDeactivationModalOpen(false)}
          handleDeactivation={() => {
            toggleMdm(true);
          }}
        />
      </Modal.Root>
      <Modal.Root
        open={confirmTransferOwnershipModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmTransferOwnershipModalOpen(false);
          }
        }}
      >
        {ownerRole && (
          <TransferOwnershipModal
            member={member}
            closeModal={() => setConfirmTransferOwnershipModalOpen(false)}
            handleTransfer={async () => {
              try {
                await transferOwnership.mutateAsync({
                  memberId: member.id,
                });

                toast.custom(
                  <NotificationBox
                    variant="success"
                    title="Ownership transferred"
                    className="max-w-md shadow-md"
                  >
                    <Typography variant="body-md">
                      {member.name || member.email} now has the Owner role.
                      Refreshing your permissions...
                    </Typography>
                  </NotificationBox>
                );

                // Wait a moment for the toast to be visible, then refresh claims.
                // If silent refresh fails, fall back to explicit logout.
                setTimeout(() => {
                  void (async () => {
                    try {
                      await authHelpers.getAccessTokenSilently(auth0, {
                        cacheMode: 'off',
                      });
                      await Promise.all([
                        queryClient.invalidateQueries({
                          queryKey: queryAccessTokenClaims.queryKey,
                        }),
                        queryClient.invalidateQueries({
                          queryKey: queryKeys.user.claims,
                        }),
                      ]);
                    } catch (error) {
                      Sentry.captureException(error);
                      await logoutAfterOwnershipTransfer(auth0);
                    }
                  })();
                }, 1500);
              } catch (error) {
                Sentry.captureException(error);
                throw error;
              }
            }}
          />
        )}
      </Modal.Root>
    </>
  );
}
